<template>
  <div v-if="loaded">
    <div class="container h-100 content">
      <div class="d-flex justify-content-center h-100">
        <div class="login block link-block">
          <div>
            <h3>Bill Payment</h3>

            <div v-if="isError" class="alert-box" :class="error.class">
              <div class="title">
                {{ error.title }}
              </div>

              <p>
                {{ error.desc }}
              </p>
            </div>

            <div v-else>
              <p>
                Below are the details of your current bill(s) for
                <strong>&pound;{{ paymentData.total }}</strong
                >. Please verify the details below and pay what is required.
              </p>

              <h4>Bill Details</h4>
              <div
                class="bills-block"
                v-bind:key="invoice.invoice"
                v-for="invoice in activeInvoices"
              >
                <div class="invoice">{{ invoice.invoice }}</div>
                <div class="price">&pound;{{ invoice.total }}</div>
              </div>

              <h4>Paid to Date</h4>
              <div v-if="paymentComplete" class="paid-block is-paid">
                &pound;{{ paidAmount }}
              </div>
              <div
                v-else
                class="paid-block"
                v-bind:class="{ 'is-paid': paymentData.paid > 0 }"
              >
                &pound;{{ paymentData.paid }}
              </div>

              <h4>Make Payment</h4>
              <p v-if="!paymentComplete">
                You can make full or partial payment below. If required you may
                also overpay to cover any other outstanding balances.
              </p>

              <PaymentChoice
                ref="paymentChoice"
                gtag-reference="BillingRequest"
                :allow-further-payment="false"
                :is-credit="true"
                :multi-pay="true"
                @handleRequest="requestPayment"
                @complete="resetPayment"
              ></PaymentChoice>

              <div class="footer-info">
                <p>
                  If you need any assistance please contact the customer
                  services team via our
                  <a href="https://dataenergy.co.uk/residents" target="_blank"
                    >Residents Hub</a
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { faSpinner, faAlignLeft } from "@fortawesome/free-solid-svg-icons";
import PaymentChoice from "@/views/Components/Payment/PaymentChoice.vue";

library.add(fas, faSpinner, faAlignLeft);

export default {
  name: "CreditRequestToPay",
  components: { PaymentChoice },
  props: ["id"],
  data() {
    return {
      paymentProcessing: false,
      loaded: false,
      paymentData: {},
      isError: false,
      error: {
        title: "",
        desc: "",
        class: ""
      },
      paymentComplete: false,
      paymentLoading: false
    };
  },
  computed: {
    env() {
      return process.env.VUE_APP_ENV;
    },
    paidAmount() {
      return parseFloat(+this.paymentData.paid + +this.requestAmount).toFixed(
        2
      );
    },
    activeInvoices() {
      let invoices = [];
      /*this.paymentData.invoices.forEach(function(row) {

      });*/
      for (const key in this.paymentData.invoices) {
        if (!this.paymentData.invoices[key].is_active) {
          continue;
        }
        invoices.push({
          invoice: key,
          total: this.paymentData.invoices[key].total
        });
      }

      return invoices;
    }
  },
  created() {
    this.$store.dispatch("loaderCancel");
    this.fetchData();
  },
  methods: {
    resetPayment() {
      this.paymentProcessing = false;
      this.paymentComplete = true;
    },
    requestPayment({ amount, paymentType }) {
      this.paymentLoading = true;
      this.paymentProcessing = true;

      this.$store
        .dispatch("initiateCreditPayment", {
          reference: this.id,
          verifier: this.paymentData.verifier,
          amount: amount,
          paymentType: paymentType
        })
        .then(response => {
          this.$gtag.event("payment", {
            event_category: "requestToPay",
            event_label: amount
          });

          this.$refs.paymentChoice.loadPayment(response.ref);
        })
        .catch(err => {
          const title = "Payment Error";
          const subtitle = err;
          const samePage = true;
          this.$store.dispatch("pushError", { title, subtitle, samePage });
          this.$refs.paymentChoice.setPaymentError();
        })
        .finally(() => {
          this.paymentLoading = false;
        });
    },
    setError(title, desc, className) {
      this.error.title = title;
      this.error.desc = desc;
      this.error.class = className;
      this.isError = true;
    },
    clearError() {
      this.error.title = "";
      this.error.desc = "";
      this.error.class = "";
      this.isError = false;
    },
    setResponse(state) {
      if (state === "expired") {
        this.setError(
          "Expired Request",
          "This request has expired and is no longer valid",
          "warning"
        );
      } else if (state === "paid") {
        this.setError(
          "No further payment required",
          "There are no further payments to be made against this request",
          "success"
        );
      }
    },
    fetchData() {
      this.clearError();
      const title = "Fetching Payment";
      const subtitle = "Getting payment details";
      this.$store.dispatch("loaderInitiate", { title, subtitle });

      const reference = this.id;

      this.$store
        .dispatch("getCreditPaymentRequest", { reference })
        .then(response => {
          if (response.state !== "active") {
            return this.setResponse(response.state);
          }

          this.paymentData = response;
        })
        .catch(() => {
          this.setError(
            "Invalid Request",
            "Invalid link for payment. If this problem persists please contact us",
            "warning"
          );
        })
        .finally(() => {
          this.loaded = true;
          this.$store.dispatch("loaderCancel");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
h2 {
  font-size: 1.6rem !important;
  text-align: left !important;
  margin-bottom: 10px !important;
  padding-left: 10px;
  margin-top: 20px;
}

p {
  color: #777;
  font-size: 0.9rem;
}

.payment-button {
  text-align: right;
  padding-top: 15px;
}

.link-block {
  width: 650px;
  margin-top: 30px;
}

.alert-box {
  padding: 20px;
  color: white;
  margin-bottom: 15px;
  margin-top: 20px;

  .title {
    font-weight: bold;
    padding-bottom: 20px;
    font-size: 1.2rem;
  }

  p {
    color: white;
  }

  &.danger {
    background-color: #ff7b71;
  }

  &.warning {
    background-color: #ff9800;
  }

  &.success {
    background-color: #83c45d;
  }
}

.bills-block {
  .invoice {
    font-size: 0.9rem;
    color: #555;
  }
  .price {
    font-size: 1.2rem;
    font-weight: bold;
    padding-bottom: 10px;
  }
}

.paid-block {
  font-size: 1rem;
  color: #888;

  &.is-paid {
    font-size: 1.2rem;
    font-weight: bold;
    color: #444;
  }
}

h4 {
  margin-top: 15px;
}

.footer-info {
  margin-top: 40px;

  p {
    font-size: 0.8rem;
    color: #888;
    margin-bottom: 0px;
  }
}
</style>
